import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import GameCard from './GameCard';
import BetSlip from './BetSlip';
import { useGameContext } from '../context/GameContext';
import { useCurrentWeek } from '../context/CurrentWeekContext';
import WeekControls from './WeekControls';
import styles from '../styles/WeeklyPicks.module.css';
import { getSocket, initSocket } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatGameDateTime } from '../utils/timeUtils';
import { useNavigate } from 'react-router-dom';

// Utility functions outside component
const getGameStatus = (game) => {
  if (game.game_status === 'completed') return 'completed';
  if (game.game_status === 'in_progress') return 'inProgress';
  return 'scheduled';
};

const sortGamesByDate = (games) => {
  return [...games].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
};

const createLockedGamesMap = (sortedGames, locksCache, displayedWeek) => {
  const lockedGamesMap = {
    inProgress: [],
    scheduled: [],
    completed: []
  };

  sortedGames.forEach(game => {
    const hasLock = (locksCache[displayedWeek] || []).find(lock => lock.gameId === game.id);
    if (hasLock) {
      const status = getGameStatus(game);
      lockedGamesMap[status].push({
        ...game,
        lockTime: hasLock.createdAt
      });
    }
  });

  return lockedGamesMap;
};

const WeeklyPickSheet = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const locksRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const {
    games,
    userPicks,
    animatingGames,
    fetchGames,
    handlePickUpdate,
    fetchLiveGameData,
    watchedPicks,
    fetchWatchedPicks,
    setWatchedPicks,
    isPeeking,
    fetchLocksForWeek,
    locksCache,
    byeWeekState,
    fetchUserSeasonLocks,
  } = useGameContext();

  const { currentWeek, calendar, isLoading: isWeekAndCalendarLoading, fetchCurrentWeekAndCalendar } = useCurrentWeek();
  const [displayedWeek, setDisplayedWeek] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isBetSlipOpen, setIsBetSlipOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isGameCardsReady, setIsGameCardsReady] = useState(false);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [isWeekChanging, setIsWeekChanging] = useState(false);
  const [isGameDataReady, setIsGameDataReady] = useState(false);

  // Step 1: Initialize socket and fetch current week and calendar
  useEffect(() => {
    const initialize = async () => {
      console.log('1. Initializing socket and fetching current week and calendar');
      if (user) {
        initSocket();
        await fetchCurrentWeekAndCalendar();
        setIsInitialized(true);
        setIsLoading(false);
      }
    };
    initialize();
  }, [user, fetchCurrentWeekAndCalendar]);

  // Step 2: Set displayed week to current week
  useEffect(() => {
    if (isInitialized && currentWeek !== null && currentWeek !== undefined && displayedWeek === null) {
      console.log('2. Setting displayed week to current week:', currentWeek);
      setDisplayedWeek(currentWeek);
    }
  }, [isInitialized, currentWeek, displayedWeek]);

  // Step 3: Fetch games for the displayed week
  useEffect(() => {
    if (displayedWeek !== null && displayedWeek !== undefined) {
      setIsGameDataReady(false);
      console.log('3. Fetching games, watched picks, and requesting locks for week:', displayedWeek);
      fetchGames(displayedWeek);
      fetchLiveGameData();
      fetchWatchedPicks(displayedWeek);
    }
  }, [displayedWeek, fetchGames, fetchLiveGameData, fetchWatchedPicks]);

  useEffect(() => {
    if (displayedWeek !== null && displayedWeek !== undefined) {
      const timer = setTimeout(() => {
        fetchLocksForWeek(displayedWeek);
      }, 1000); // 1 second delay

      return () => clearTimeout(timer);
    }
  }, [displayedWeek, fetchLocksForWeek]);

  useEffect(() => {
    if (displayedWeek !== null && displayedWeek !== undefined) {
      // Filter watched picks for the displayed week
      const filteredWatchedPicks = watchedPicks.filter(pick => pick.weekNumber === displayedWeek);
      if (JSON.stringify(filteredWatchedPicks) !== JSON.stringify(watchedPicks)) {
        setWatchedPicks(filteredWatchedPicks);
      }
    }
  }, [displayedWeek, watchedPicks, setWatchedPicks]);

  // Step 4: Setup socket listeners
  useEffect(() => {
    if (isInitialized) {
      const socket = getSocket();
      if (socket) {
        socket.on('pick_update', handlePickUpdate);
        return () => {
          socket.off('pick_update', handlePickUpdate);
        };
      }
    }
  }, [isInitialized, handlePickUpdate]);

  // New useEffect to set game cards ready
  useEffect(() => {
    if (games.length > 0 && !isGameCardsReady) {
      setIsGameCardsReady(true);
    }
  }, [games, isGameCardsReady]);

  useEffect(() => {
    if (isInitialized && currentWeek !== null && games.length > 0) {
      setIsFullyLoaded(true);
    }
  }, [isInitialized, currentWeek, games]);

  useEffect(() => {
    if (games.length > 0 && locksCache[displayedWeek]) {
      // Add a small delay to ensure DOM updates are complete
      const timer = setTimeout(() => {
        setIsGameDataReady(true);
        setIsWeekChanging(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [games, locksCache, displayedWeek]);

  const changeWeek = useCallback((direction) => {
    const visibleWeeks = calendar.filter(week => !week.hide_week);
    const currentIndex = visibleWeeks.findIndex(week => week.week === displayedWeek);
    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

    if (newIndex >= 0 && newIndex < visibleWeeks.length) {
      setIsWeekChanging(true);
      setIsGameDataReady(false);
      const newWeek = visibleWeeks[newIndex].week;
      setDisplayedWeek(newWeek);
      fetchWatchedPicks(newWeek);
      fetchLocksForWeek(newWeek);
      fetchUserSeasonLocks();
    }
  }, [calendar, displayedWeek, fetchWatchedPicks, fetchLocksForWeek, fetchUserSeasonLocks]);

  const getWeekLabel = useCallback((week) => {
    return week.notes || `Week ${week.week}`;
  }, []);

  const visibleWeeks = useMemo(() =>
    calendar ? calendar.filter(week => !week.hide_week) : []
  , [calendar]);

  const handleCurrentWeek = useCallback(() => {
    setIsWeekChanging(true);
    setDisplayedWeek(currentWeek);
    fetchWatchedPicks(currentWeek);
    fetchLocksForWeek(currentWeek);
    fetchUserSeasonLocks();
  }, [currentWeek, fetchWatchedPicks, fetchLocksForWeek, fetchUserSeasonLocks]);

  // Update the grouping and sorting logic
  const { lockedGames, groupedGames } = useMemo(() => {
    const sortedGames = sortGamesByDate(games);
    const lockedGamesMap = createLockedGamesMap(sortedGames, locksCache, displayedWeek);

    // Sort each category of locked games by lock time
    Object.keys(lockedGamesMap).forEach(status => {
      lockedGamesMap[status].sort((a, b) => new Date(a.lockTime) - new Date(b.lockTime));
    });

    // Combine locked games in the desired order
    const locked = [
      ...lockedGamesMap.inProgress,
      ...lockedGamesMap.scheduled,
      ...lockedGamesMap.completed
    ];

    // Group remaining games by date
    const grouped = {};

    sortedGames.forEach(game => {
      const { groupDate } = formatGameDateTime(game.start_date);
      if (!grouped[groupDate]) {
        grouped[groupDate] = {
          inProgress: [],
          scheduled: [],
          completed: [],
          isCompleted: false
        };
      }

      const status = getGameStatus(game);
      grouped[groupDate][status].push(game);
    });

    // Check if each day is completed
    Object.keys(grouped).forEach(date => {
      if (grouped[date].inProgress.length === 0 &&
          grouped[date].scheduled.length === 0) {
        grouped[date].isCompleted = true;
      }
    });

    // Sort dates to put completed days at the end
    const sortedDates = Object.keys(grouped).sort((a, b) => {
      if (grouped[a].isCompleted && !grouped[b].isCompleted) return 1;
      if (!grouped[a].isCompleted && grouped[b].isCompleted) return -1;
      return new Date(a) - new Date(b);
    });

    const sortedGrouped = {};
    sortedDates.forEach(date => {
      sortedGrouped[date] = grouped[date];
    });

    return {
      lockedGames: locked,
      groupedGames: sortedGrouped
    };
  }, [games, locksCache, displayedWeek]);

  // Add helper function to get animation class
  const getAnimationClass = useCallback((gameId) => {
    const animationState = animatingGames[gameId];
    if (animationState) {
      return styles[`game${animationState.charAt(0).toUpperCase() + animationState.slice(1)}`];
    }
    return '';
  }, [animatingGames]);

  // Update the renderGameCards function to use the new structure
  const renderGameCards = useCallback(() => {
    if (!games || games.length === 0) {
      return <div>No games available for this week.</div>;
    }

    const watchedPicksForWeek = watchedPicks.filter(pick => pick.weekNumber === displayedWeek);

    return (
      <>
        {/* League Locks Section */}
        {lockedGames.length > 0 && (
          <React.Fragment>
            <div ref={locksRef} id="league-locks-section" className={styles.dateHeader}>
              League Locks
            </div>
            {lockedGames.map(game => (
              <GameCard
                key={game.id}
                game={game}
                userPick={findUserPick(userPicks, game.id)}
                watchedPicks={watchedPicksForWeek}
                displayedWeek={displayedWeek}
                isBetSlipOpen={isBetSlipOpen}
                className={getAnimationClass(game.id)}
              />
            ))}
          </React.Fragment>
        )}

        {/* Regular Games Section */}
        {Object.entries(groupedGames).map(([date, statusGroups]) => (
          <React.Fragment key={date}>
            <div className={styles.dateHeader}>
              {formatGameDateTime(date).headerDate}
            </div>
            {/* In Progress Games */}
            {statusGroups.inProgress.map(game => (
              <GameCard
                key={game.id}
                game={game}
                userPick={findUserPick(userPicks, game.id)}
                watchedPicks={watchedPicksForWeek}
                displayedWeek={displayedWeek}
                isBetSlipOpen={isBetSlipOpen}
                className={getAnimationClass(game.id)}
              />
            ))}
            {/* Scheduled Games */}
            {statusGroups.scheduled.map(game => (
              <GameCard
                key={game.id}
                game={game}
                userPick={findUserPick(userPicks, game.id)}
                watchedPicks={watchedPicksForWeek}
                displayedWeek={displayedWeek}
                isBetSlipOpen={isBetSlipOpen}
                className={getAnimationClass(game.id)}
              />
            ))}
            {/* Completed Games */}
            {statusGroups.completed.map(game => (
              <GameCard
                key={game.id}
                game={game}
                userPick={findUserPick(userPicks, game.id)}
                watchedPicks={watchedPicksForWeek}
                displayedWeek={displayedWeek}
                isBetSlipOpen={isBetSlipOpen}
                className={getAnimationClass(game.id)}
              />
            ))}
          </React.Fragment>
        ))}
      </>
    );
  }, [games, userPicks, watchedPicks, displayedWeek, isBetSlipOpen, lockedGames, groupedGames, getAnimationClass]);

  const enrichWatchedPicks = useCallback((watchedPicks) => {
    return watchedPicks.map(pick => {
      const game = games.find(g => g.id === pick.gameId);
      if (game) {
        const homeTeam = typeof game.home_team === 'object' ? game.home_team.name : game.home_team;
        const awayTeam = typeof game.away_team === 'object' ? game.away_team.name : game.away_team;
        return {
          ...pick,
          team: pick.pickType === 'home' ? homeTeam : awayTeam,
          opponent: pick.pickType === 'home' ? awayTeam : homeTeam,
          gameDate: game.start_date,
          watchLine: pick.watchLine || 0,
          liveLine: pick.liveLine || (pick.pickType === 'home' || pick.pickType === 'away' ? game.spread : game.over_under)
        };
      }
      return {
        ...pick,
        team: 'Unknown Team',
        opponent: 'Unknown Opponent',
        gameDate: 'Date TBD',
        watchLine: pick.watchLine || 0,
        liveLine: pick.liveLine || 0
      };
    });
  }, [games]);

  const enrichLockedPicks = useCallback((lockedPicks) => {
    return lockedPicks.map(lock => {
      return {
        ...lock,
        team: lock.pickType === 'home' ? lock.homeTeam : lock.awayTeam,
        opponent: lock.pickType === 'home' ? lock.awayTeam : lock.homeTeam,
      };
    });
  }, []);

  const toggleBetSlip = useCallback(() => {
    setIsBetSlipOpen(prevState => {
      return !prevState;
    });
  }, []);

  const renderBetSlip = useCallback(() => {
    if (displayedWeek !== null && calendar && calendar.length > 0) {
      const watchedPicksForWeek = watchedPicks.filter(pick => pick.weekNumber === displayedWeek);
      const enrichedWatchedPicks = enrichWatchedPicks(watchedPicksForWeek);
      const locksForWeek = locksCache[displayedWeek] || [];
      const enrichedLocks = enrichLockedPicks(locksForWeek);
      return (
        <BetSlip
          week={displayedWeek}
          calendar={calendar}
          onClose={() => setIsBetSlipOpen(false)}
          watchedPicks={enrichedWatchedPicks}
          setWatchedPicks={setWatchedPicks}
          toggleOpen={toggleBetSlip}
          locks={enrichedLocks}
          userId={user.id}
          isBetSlipOpen={isBetSlipOpen}
          byeWeekState={byeWeekState}
          games={games}
        />
      );
    }
    return null;
  }, [
    displayedWeek,
    calendar,
    watchedPicks,
    enrichWatchedPicks,
    setWatchedPicks,
    toggleBetSlip,
    locksCache,
    enrichLockedPicks,
    user,
    isBetSlipOpen,
    byeWeekState,
    games
  ]);

  useEffect(() => {
    if (isWeekChanging && games.length > 0 && isGameCardsReady) {
      setIsWeekChanging(false);
    }
  }, [isWeekChanging, games.length, isGameCardsReady]);

  // Extract the find function to a separate helper
  const findUserPick = (userPicks, gameId) => userPicks.find(pick => pick.game_id === gameId);

  return (
    <div className={styles.weeklyPickSheet}>
      <WeekControls
        displayedWeek={displayedWeek}
        visibleWeeks={visibleWeeks}
        currentWeek={currentWeek}
        changeWeek={changeWeek}
        setDisplayedWeek={(week) => {
          setIsWeekChanging(true);
          setIsGameDataReady(false);
          setDisplayedWeek(week);
          fetchWatchedPicks(week);
          fetchLocksForWeek(week);
          fetchUserSeasonLocks();
        }}
        fetchWatchedPicks={fetchWatchedPicks}
        handleCurrentWeek={handleCurrentWeek}
        getWeekLabel={getWeekLabel}
      />

      <div className={styles.contentPadding}>
        {(isLoading ||
          isWeekAndCalendarLoading ||
          !isGameCardsReady ||
          !isFullyLoaded ||
          isWeekChanging ||
          !isGameDataReady) ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading...</p>
          </div>
        ) : (
          <div className={styles.gameGrid}>
            {renderGameCards()}
          </div>
        )}
      </div>

      <button className={styles.pickSlipButton} onClick={toggleBetSlip}>
        {isBetSlipOpen ? 'Close PickSlip' : 'Open PickSlip'}
      </button>

      <div className={`${styles.betSlipContainer} ${isBetSlipOpen ? styles.open : ''} ${isPeeking ? styles.peeking : ''}`}>
        {renderBetSlip()}
      </div>
    </div>
  );
};

export default React.memo(WeeklyPickSheet);
